import { useQuery } from '@tanstack/react-query';
import { Space, Tabs, Typography } from 'antd';
import { useEstadoDeLista } from '@titularis/ui';
import { api } from '../../api';
import { useSession } from '../../auth';
import { AcessosDasPessoas } from './AcessosDasPessoas';
import { FunilDasPessoas } from './FunilDasPessoas';
import { ListaDePessoas } from './ListaDePessoas';
import { ListasDeEspera } from './ListasDeEspera';
import { OrigemDasPessoas } from './OrigemDasPessoas';
import { RecorrenciaDasPessoas } from './RecorrenciaDasPessoas';

/**
 * Pessoas: uma seção só, onde havia três telas que não se falavam.
 *
 * **O que mudou, e por quê.** Até 18/09/2026 o dado de gente estava repartido
 * entre "Administradores" (toda conta, com o papel dela), "Assinantes" (uma
 * linha por direito) e "Origem" (contagem por canal). Cada uma tinha a própria
 * busca, a própria tabela e o próprio recorte, e as três liam a mesma
 * população - de modo que responder "quem criou conta esta semana e já está
 * estudando?" era abrir duas abas e casar e-mail com o olho. O lançamento do
 * GTM tornou essa a pergunta do dia.
 *
 * O que as unifica não é o layout, é o **registro**: as três falam de pessoas, e
 * a pessoa passa a ter ficha própria (`/pessoas/:id`). As abas são perguntas
 * distintas sobre a mesma gente:
 *
 * 1. **Pessoas** - uma linha por gente: quem é, onde parou, o que fez;
 * 2. **Recorrência** - uma linha por semana de entrada: quantos voltam a estudar;
 * 3. **Acessos** - uma linha por par pessoa-produto: quem tem acesso a *quê*;
 * 4. **Origem** - uma linha por canal: de onde essa gente veio, e quanto rendeu;
 * 5. **Listas de espera** - uma linha por pedido de aviso: quem espera o quê.
 *
 * A Recorrência chegou em 29/09/2026 e fica logo depois de Pessoas porque é a
 * mesma pergunta em outra escala: a coluna da lista diz se *esta* pessoa volta,
 * e a aba diz se a base volta. É o mesmo par que a coluna Origem forma com a
 * aba Origem.
 *
 * A quarta chegou em 25/09/2026, vinda de uma tela própria (`/listas`). Ela é o
 * degrau "Na lista" do funil visto por dentro, e longe dele os dois números
 * divergiam: lá a equipe que testou o formulário contava, aqui não.
 *
 * O funil fica **acima** das abas, e não dentro da primeira, porque descreve a
 * mesma população em todas: trocar de aba não muda quantas contas existem.
 *
 * **Tudo aqui é do Dono**, e a guarda é de rota (`RequireOwner` no `App.tsx`)
 * por cima da guarda da API. As três telas anteriores já eram, cada uma pelo seu
 * motivo; juntas, o motivo é um só e é o mais forte dos três - esta seção lê
 * dado pessoal de aluno, e não só o que ele comprou. A lista de espera entrou
 * pelo mesmo motivo, e perdeu no caminho quem só publica conteúdo: é lista de
 * e-mail de gente.
 */
export function PessoasPage() {
  const { ler, filtrar } = useEstadoDeLista();
  const { data: sessao } = useSession();
  const ehDono = sessao?.user.role === 'owner';

  const aba = ler.texto('aba') ?? 'pessoas';

  /*
   * O resumo é da seção, e não da aba: ele vive aqui para não ser buscado de
   * novo a cada troca. A lista de canais vem junto dele porque sai da mesma
   * varredura - pedir os canais numa segunda rota seria uma ida ao banco para
   * preencher um `Select`.
   */
  const resumo = useQuery({
    queryKey: ['resumo-das-pessoas'],
    queryFn: () => api.peopleSummary(),
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Pessoas
        </Typography.Title>
        <Typography.Text type="secondary">
          Quem chegou, em que etapa parou e o que está fazendo. Também é daqui que se concede
          acesso, se muda o nível e se age sobre uma conta.
        </Typography.Text>
      </div>

      <FunilDasPessoas resumo={resumo.data} carregando={resumo.isPending} />

      <Tabs
        activeKey={aba}
        /*
         * A aba entra na URL como os filtros, e pelo mesmo motivo: recarregar a
         * página, favoritar e mandar o link têm de devolver o que se estava
         * vendo. `filtrar` também limpa a paginação, que é o certo - a página 3
         * de Pessoas não é a página 3 de Acessos.
         */
        onChange={(chave) => filtrar({ aba: chave === 'pessoas' ? undefined : chave })}
        items={[
          {
            key: 'pessoas',
            label: 'Pessoas',
            children: <ListaDePessoas canais={resumo.data?.canais ?? []} />,
          },
          {
            key: 'recorrencia',
            label: 'Recorrência',
            children: <RecorrenciaDasPessoas />,
          },
          {
            key: 'acessos',
            label: 'Acessos',
            children: <AcessosDasPessoas ehDono={ehDono} />,
          },
          {
            key: 'origem',
            label: 'Origem',
            children: <OrigemDasPessoas />,
          },
          {
            key: 'listas',
            label: 'Listas de espera',
            children: <ListasDeEspera />,
          },
        ]}
        /*
         * Só a aba ativa é montada. Cada uma faz consultas próprias, e montar
         * todas de uma vez faria abrir a seção disparar meia dúzia de
         * requisições para pintar uma tabela.
         */
        destroyOnHidden
      />
    </Space>
  );
}
